import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import DraftCardShell, { primaryButtonSx, textButtonSx } from "../drafts/DraftCardShell";
import { instructionsText, totalQuestions } from "../drafts/quizDraftOps";
import { previewAssignmentQuestionsCall } from "../../../redux/api/quizSystem.api";
import QuizQuestionPreviewPanel from "../../../prof/QuizSystem/QuizQuestionPreviewPanel";

const TOGGLES = [
  ["shuffle_questions", "Shuffle questions"],
  ["shuffle_answers", "Shuffle answers"],
  ["show_score_immediately", "Show score right away"],
  ["show_answers_after", "Show answers after submitting"],
];

function ruleLabel(rule) {
  if (rule.bank_source === "private") return rule.topic_label || "My question bank";
  const cat = rule.catalogue ? rule.catalogue.charAt(0).toUpperCase() + rule.catalogue.slice(1) : "Microlab";
  const subs = Array.isArray(rule.subtitle_labels) && rule.subtitle_labels.length ? rule.subtitle_labels.join(", ") : rule.topic_label || "All topics";
  return `${cat} · ${subs}`;
}

function buildPreviewRules(rules = []) {
  return rules
    .map((r) => ({
      bank_source: r.bank_source === "microlab" ? "microlab" : "private",
      catalogue: r.bank_source === "microlab" ? r.catalogue || undefined : undefined,
      quiz_title_ids:
        r.bank_source === "microlab" && Array.isArray(r.quiz_title_ids) && r.quiz_title_ids.length
          ? r.quiz_title_ids
          : undefined,
      professor_topic_id: r.bank_source === "microlab" ? undefined : r.professor_topic_id || undefined,
      topic_id: r.bank_source === "microlab" ? undefined : r.topic_id || undefined,
      num_questions: Number(r.num_questions) || 0,
      difficulty_filter: r.bank_source === "microlab" ? undefined : r.difficulty_filter || undefined,
    }))
    .filter(
      (r) =>
        Number(r.num_questions) > 0 &&
        (r.bank_source === "microlab" ? r.catalogue : r.professor_topic_id || r.topic_id)
    );
}

/**
 * In-chat quiz configuration. Edits change the draft; "Create quiz" re-checks the
 * exact configuration shown and then creates it (approval required).
 */
export default function QuizDraftCard({ draft, actions, confirming, disabled, startExpanded }) {
  const [expanded, setExpanded] = useState(Boolean(startExpanded));
  const [title, setTitle] = useState(draft.title || "");
  const [minutes, setMinutes] = useState(String(draft.time_limit_minutes || 30));
  const [instructions, setInstructions] = useState(instructionsText(draft.rules_html));
  const [preview, setPreview] = useState({
    loading: false,
    error: "",
    questions: [],
    requested: 0,
    pool_total: 0,
    picked: 0,
  });
  const previewRequestRef = useRef(0);

  useEffect(() => {
    if (startExpanded) setExpanded(true);
  }, [startExpanded]);

  // Follow changes made by chat turns or undo while the fields aren't focused.
  useEffect(() => setTitle(draft.title || ""), [draft.title]);
  useEffect(() => setMinutes(String(draft.time_limit_minutes || 30)), [draft.time_limit_minutes]);
  useEffect(() => setInstructions(instructionsText(draft.rules_html)), [draft.rules_html]);

  const rules = draft.topic_rules || [];
  const total = totalQuestions(draft);
  const busy = disabled || confirming;
  const hasLockedPick = Array.isArray(draft.locked_question_refs) && draft.locked_question_refs.length > 0;

  const loadPreview = async ({ randomize } = {}) => {
    const topicRules = buildPreviewRules(draft.topic_rules);
    if (!topicRules.length) {
      setPreview({
        loading: false,
        error: "",
        questions: [],
        requested: 0,
        pool_total: 0,
        picked: 0,
      });
      actions.setLockedRefs?.([]);
      return;
    }
    const requestId = ++previewRequestRef.current;
    setPreview((prev) => ({ ...prev, loading: true, error: "" }));
    const shouldRandomize =
      typeof randomize === "boolean" ? randomize : draft.shuffle_questions !== false;
    const res = await previewAssignmentQuestionsCall({
      topic_rules: topicRules,
      randomize: shouldRandomize,
    });
    if (requestId !== previewRequestRef.current) return;
    if (res?.status && res.status >= 400) {
      setPreview({
        loading: false,
        error: res?.message || "Could not preview questions for these rules.",
        questions: [],
        requested: 0,
        pool_total: 0,
        picked: 0,
      });
      actions.setLockedRefs?.([]);
      return;
    }
    const data = res?.data || {};
    const locked = Array.isArray(data.locked_question_refs) ? data.locked_question_refs : [];
    setPreview({
      loading: false,
      error: "",
      questions: Array.isArray(data.questions) ? data.questions : [],
      requested: Number(data.requested) || 0,
      pool_total: Number(data.pool_total) || 0,
      picked: Number(data.picked) || 0,
    });
    actions.setLockedRefs?.(locked);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      void loadPreview({ randomize: draft.shuffle_questions !== false });
    }, 350);
    return () => clearTimeout(timer);
  }, [draft.topic_rules, draft.shuffle_questions]);

  return (
    <DraftCardShell
      kicker={draft.awaiting_confirmation ? "QUIZ READY TO CREATE" : "QUIZ DRAFT"}
      title={draft.title || "Untitled quiz"}
      summary={`${total} question${total === 1 ? "" : "s"} · ${draft.time_limit_minutes || 30} min · ${rules.length} rule${rules.length === 1 ? "" : "s"}`}
      icon={<QuizOutlinedIcon fontSize="small" />}
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
      footer={
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Button
            size="small"
            variant="contained"
            disableElevation
            disabled={busy || !rules.length || preview.loading || !hasLockedPick}
            onClick={actions.confirm}
            sx={primaryButtonSx}
          >
            {confirming ? "Creating…" : "Create quiz"}
          </Button>
          {!expanded ? (
            <Button size="small" disabled={busy} onClick={() => setExpanded(true)} sx={textButtonSx}>
              Configure
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={actions.openFullForm} sx={{ ...textButtonSx, fontSize: 11, color: "#64748b" }}>
            Full form
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1.1}>
        <TextField
          size="small"
          label="Quiz title"
          value={title}
          disabled={busy}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== (draft.title || "") && actions.updateSettings({ title })}
          fullWidth
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          size="small"
          type="number"
          label="Time limit (minutes)"
          value={minutes}
          disabled={busy}
          onChange={(e) => setMinutes(e.target.value)}
          onBlur={() => Number(minutes) !== Number(draft.time_limit_minutes) && actions.updateSettings({ time_limit_minutes: minutes })}
          inputProps={{ min: 1, max: 300 }}
          sx={{ maxWidth: 180 }}
        />

        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", mb: 0.5 }}>QUESTIONS</Typography>
          <Stack spacing={0.6}>
            {rules.map((rule, index) => (
              <Stack
                // eslint-disable-next-line react/no-array-index-key
                key={`${rule.bank_source}-${rule.catalogue}-${index}`}
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ p: 0.75, borderRadius: 1.5, border: "1px solid rgba(148,163,184,0.3)" }}
              >
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "#0f172a" }} noWrap title={ruleLabel(rule)}>
                  {ruleLabel(rule)}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={rule.num_questions}
                  disabled={busy}
                  onChange={(e) => actions.updateRule(index, { num_questions: e.target.value })}
                  inputProps={{ min: 1, max: 50, "aria-label": "Number of questions" }}
                  sx={{ width: 68 }}
                />
                <TextField
                  select
                  size="small"
                  value={rule.difficulty_filter || "any"}
                  disabled={busy}
                  onChange={(e) => actions.updateRule(index, { difficulty_filter: e.target.value })}
                  inputProps={{ "aria-label": "Difficulty" }}
                  sx={{ width: 98 }}
                >
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="easy">Easy</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="hard">Hard</MenuItem>
                </TextField>
                <IconButton size="small" aria-label="Remove rule" disabled={busy || rules.length <= 1} onClick={() => actions.removeRule(index)} sx={{ color: "#b91c1c" }}>
                  <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
            {actions.changeTitles ? (
              <Button size="small" disabled={busy} onClick={actions.changeTitles} sx={{ ...textButtonSx, fontSize: 12 }}>
                Change titles
              </Button>
            ) : null}
            <Typography sx={{ fontSize: 11, color: "#64748b" }}>
              Or ask in the chat — e.g. “also add 5 embryology questions”.
            </Typography>
          </Stack>
        </Box>

        <Box>
          {TOGGLES.map(([key, label]) => (
            <FormControlLabel
              key={key}
              sx={{ display: "flex", m: 0, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
              control={
                <Switch size="small" checked={draft[key] !== false} disabled={busy} onChange={(e) => actions.updateSettings({ [key]: e.target.checked })} />
              }
              label={label}
            />
          ))}
        </Box>

        <QuizQuestionPreviewPanel
          dense
          questions={preview.questions}
          requested={preview.requested}
          poolTotal={preview.pool_total}
          picked={preview.picked}
          loading={preview.loading}
          error={preview.error}
          onRandomize={() => loadPreview({ randomize: true })}
          onReload={() => loadPreview({ randomize: draft.shuffle_questions !== false })}
        />

        <TextField
          size="small"
          label="Instructions for students"
          value={instructions}
          disabled={busy}
          onChange={(e) => setInstructions(e.target.value)}
          onBlur={() => instructions !== instructionsText(draft.rules_html) && actions.updateSettings({ instructions })}
          fullWidth
          multiline
          minRows={2}
          maxRows={8}
        />
      </Stack>
    </DraftCardShell>
  );
}

QuizDraftCard.propTypes = {
  draft: PropTypes.object.isRequired,
  actions: PropTypes.shape({
    confirm: PropTypes.func.isRequired,
    updateSettings: PropTypes.func.isRequired,
    updateRule: PropTypes.func.isRequired,
    removeRule: PropTypes.func.isRequired,
    openFullForm: PropTypes.func.isRequired,
    changeTitles: PropTypes.func,
    setLockedRefs: PropTypes.func,
  }).isRequired,
  confirming: PropTypes.bool,
  disabled: PropTypes.bool,
  startExpanded: PropTypes.bool,
};
